/**
 * Convert image file to base64 data URL for temporary storage
 * @param {File} file - The image file to convert
 * @returns {Promise<Object>} - Object with url (base64), name, and size
 */
export const uploadImage = async (file) => {
  try {
    // Validate file size (max 5MB for base64 storage)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('Image size must be less than 5MB');
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Convert to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve({
          url: reader.result, // base64 data URL
          name: file.name,
          size: file.size,
          type: file.type
        });
      };

      reader.onerror = () => {
        reject(new Error('Failed to read image file'));
      };

      reader.readAsDataURL(file);
    });
  } catch (error) {
    console.error('Error processing image:', error);
    throw error;
  }
};

/**
 * Convert multiple images to base64
 * @param {File[]} files - Array of image files
 * @returns {Promise<Object[]>} - Array of image objects with base64 URLs
 */
export const uploadMultipleImages = async (files) => {
  try {
    const uploadPromises = files.map(file => uploadImage(file));
    const images = await Promise.all(uploadPromises);
    return images;
  } catch (error) {
    console.error('Error processing multiple images:', error);
    throw new Error('Failed to process images. Please try again.');
  }
};
